// Loom kept the client status enum the legacy Client API used, so a card's status needs no
// translation. Everything else here is assembled from separate Loom tables that the legacy
// query document held as one denormalized blob.

import {buildFamilyMembers} from 'utils/backend/loom/family/family_adapter';

const personName = (person) => {
  return [person?.firstName, person?.lastName].filter(Boolean).join(' ');
};

const markdown = (blob) => ({blob: blob || '', editorType: 'MARKDOWN'});

// The legacy contacts array carried a free-text detail per channel and the scenes only print
// it, so Loom's two typed columns collapse back into that shape. The type strings are the
// legacy spellings deliberately: Contacts.jsx branches on the literals Email and Phone and
// files anything else under miscellaneous, so an uppercase type empties both sections and
// leaves every phone without isRelationshipContact, with nothing thrown to show for it.
const buildContacts = (person) => {
  const contacts = [];

  if (person?.email) contacts.push({type: 'Email', detail: person.email});
  if (person?.phoneNumber) contacts.push({type: 'Phone', detail: person.phoneNumber});

  return contacts;
};

// Legacy keyed relationships by relationshipRn and every scene calls Object.values on them, so
// an object is what they expect rather than an array. connectionType comes back as the Loom
// enum, not the legacy free text: the migration mapped strings like Son into the enum and
// bucketed whatever did not fit, so the original wording is not recoverable from Loom.
// description always ships, and it comes off the client: RelationshipView reads .blob off it
// during render and RelationshipEdit replaces its own defaults with this object, so both throw
// on a missing one. Legacy stored it per relationship but wrote the client's own description
// into it, identically on 2,434 of the 2,438 that have one, and Loom kept only that single copy
// as client.notes. Sourcing it back matches legacy on 2,503 of 2,590 relationships; the
// remainder are 83 that legacy left blank under a client that has a description, which nothing
// in Loom can distinguish, and 4 whose legacy text genuinely differed.
const buildRelationships = (client) => {
  return (client?.relationships || []).reduce((result, relationship) => ({
    ...result,
    [relationship.id]: {
      relationshipRn: relationship.id,
      name: personName(relationship.familyMember?.person),
      connectionType: relationship.relationship,
      priority: relationship.priority,
      // Built per relationship rather than shared, since the edit dialog holds this object
      // in state and every card would otherwise alias one instance.
      description: markdown(client?.notes),
      contacts: buildContacts(relationship.familyMember?.person),
      roles: (relationship.roles || []).map((role) => role.role).filter(Boolean),
    },
  }), {});
};

const buildCaregivers = (client) => {
  return (client?.caregiverAssignments || []).reduce((result, assignment) => ({
    ...result,
    [assignment.id]: {
      caregiverRn: assignment.caregiver?.legacyCaregiverRn,
      caregiverName: personName(assignment.caregiver?.person),
      status: assignment.status,
    },
  }), {});
};

// Legacy held the address as one free-text line on the careplan and nothing ever split it into
// components, so the migration parked it whole in address.legacyAddress: all 596 dev clients with
// an address row have it, and none has a line1, city or region. The structured columns are read
// first anyway, so this keeps working if Loom parses them later.
const buildAddress = (client) => {
  const [personAddress] = client?.person?.addresses || [];
  const address = personAddress?.address;

  if (!address) return '';

  return [address.line1, address.city, address.region].filter(Boolean).join(', ') ||
    address.legacyAddress ||
    '';
};

// Careplan.jsx renders five sections, and the first three read .blob off their leaves during
// the initial useState with no guard, so every section ships whether or not the client has a
// care_plan row. Loom's text columns are NOT NULL DEFAULT '' anyway. leadQuality sits on the
// careplan in legacy and on the client in Loom, and the lead card reads it from here.
// caregiverRequirementChips has no Loom column and stays absent, which that section tolerates.
const buildCarePlan = (client) => {
  const carePlan = client?.carePlan;

  return {
    leadQuality: client?.leadQuality,
    generalInformation: {
      clientName: personName(client?.person),
      address: buildAddress(client),
      clientInfo: markdown(carePlan?.clientInfo),
      notes: markdown(carePlan?.notes),
    },
    medicalHistory: {
      conditions: markdown(carePlan?.conditions),
      medications: markdown(carePlan?.medications),
    },
    careRoutine: {
      clientPreferences: markdown(carePlan?.clientPreferences),
      dailyTasks: markdown(carePlan?.dailyTasks),
    },
    caregiverRequirements: {
      careplanSummary: carePlan?.summary || '',
      scheduleRequirement: carePlan?.scheduleRequirement || '',
      matchDifficulty: carePlan?.matchDifficulty || '',
    },
    replacementPreference: client?.replacementPreference,
  };
};

// The card measures days in stage off this and falls back to createdAt when it is absent.
const buildLastStatusLog = (client) => {
  const [latest] = client?.statusHistory || [];

  if (!latest) return undefined;

  return {
    status: latest.status,
    createdAt: latest.createdAt,
  };
};

const buildClientDetail = (client) => ({
  clientId: client.id,
  clientRn: client.legacyClientRn,
  clientName: personName(client.person),
  status: client.status,
  relationships: buildRelationships(client),
  caregivers: buildCaregivers(client),
  clientCarePlan: buildCarePlan(client),
  clientLastLogStatusUpdate: buildLastStatusLog(client),
  replacementPreference: client.replacementPreference,
  description: client.notes ? {blob: client.notes, editorType: 'MARKDOWN'} : undefined,
  createdAt: client.createdAt,
  updatedAt: client.updatedAt,
});

// Loom's followUp enum carries the same two booleans the yes/no section reads back, and it
// reads them unguarded, so every question ships one.
const CAREVIEW_FOLLOW_UP_BY_LOOM_FOLLOW_UP = {
  ON_YES: {enabledOnYes: true, enabledOnNo: false},
  ON_NO: {enabledOnYes: false, enabledOnNo: true},
  BOTH: {enabledOnYes: true, enabledOnNo: true},
  NONE: {enabledOnYes: false, enabledOnNo: false},
};

// Multiple-choice options have no Loom column: the migration's LegacyShiftReportQuestion never
// carried them, so an answer that chose an option is the only surviving record of one. Answers
// pool across every questionnaire version because a single version can miss an option: of the
// seven the one such client has, three carry no answer and a fourth carries one of the two.
// The author's ordering is not recoverable, hence the sort.
const buildOptionsByQuestion = (client) => {
  const chosen = {};

  for (const report of client?.shiftReports || []) {
    for (const answer of report.answers || []) {
      if (answer.answerTypeSnapshot !== 'MULTIPLE_CHOICE' || !answer.answerText) continue;

      if (!chosen[answer.questionId]) chosen[answer.questionId] = new Set();
      chosen[answer.questionId].add(answer.answerText);
    }
  }

  return chosen;
};

// The legacy Careview landed in shift_report. CareviewSettings wants an object keyed by
// question, which it sorts on rankorder, and the four answer types are spelled identically on
// both sides. The query does not order a client's versions, so the active one is chosen rather
// than whichever row Postgres happened to return first.
const buildCareview = (client) => {
  const reports = client?.shiftReports || [];
  const report = reports.find((candidate) => candidate.status === 'ACTIVE') || reports[0];

  if (!report) return {};

  const optionsByQuestion = buildOptionsByQuestion(client);

  return {
    careviewRn: report.id,
    careviewQuestions: (report.questionItems || []).reduce((result, item) => ({
      ...result,
      [item.questionId]: {
        questionRn: item.questionId,
        type: item.question?.answerType,
        rankorder: item.sortOrder,
        questionData: {
          questionTitle: item.question?.prompt || '',
          isRequired: item.required,
          followUpYesNo:
            CAREVIEW_FOLLOW_UP_BY_LOOM_FOLLOW_UP[item.question?.followUp] ||
            CAREVIEW_FOLLOW_UP_BY_LOOM_FOLLOW_UP.NONE,
          // CareViewItemNew filters this unguarded on a MULTIPLE_CHOICE question, so it ships on
          // every one of them even when no answer has yet named an option.
          ...(item.question?.answerType === 'MULTIPLE_CHOICE' && {
            options: [...(optionsByQuestion[item.questionId] || [])]
                .sort((a, b) => a.localeCompare(b))
                .map((optionTitle) => ({optionTitle, followUp: false})),
          }),
        },
      },
    }), {}),
  };
};

// Forms.jsx keys its schedule merging on formKey and on a fieldType per answer. Loom's intake
// submissions carry neither, so there is nothing to map yet and an empty list is the honest
// answer; a half-built form would render answers under raw question codes.
const buildForms = () => [];

// Legacy sent one status value and paged with an integer offset; Loom takes a list and a row
// cursor, so page boundaries never line up between the two. No limit is sent: the endpoint
// picks the page size, and overriding it here would put that decision in two places.
export const toLoomClientListQuery = (filters, sort, cursor) => {
  const query = {
    cursor,
    sortDirection: sort?.sortDirection?.toLowerCase() || 'desc',
  };
  const statuses = [];

  for (const filter of filters || []) {
    if (filter.key === 'status') {
      const values = Array.isArray(filter.value) ? filter.value : [filter.value];
      statuses.push(...values.filter((status) => status && status !== 'ALL'));
    }

    if (filter.key === 'searchString' && filter.value) query.search = filter.value;
  }

  if (statuses.length) query.status = [...new Set(statuses)].join(',');

  return query;
};

export const adaptLoomClientList = (response) => ({
  list: (response?.clients || []).map(buildClientDetail),
  lastEvaluatedKey: response?.nextCursor || null,
});

export const adaptLoomClientDetail = (client) => ({
  clientRn: client.legacyClientRn,
  clientDetail: {
    ...buildClientDetail(client),
    familyName: client.family.name,
    familyMembers: buildFamilyMembers(client),
    familyNotes: client.family.notes,
    clientNotes: client.notes,
  },
  careplan: buildCarePlan(client),
  careview: buildCareview(client),
  forms: buildForms(),
});
